import type { ReactElement } from "react";
import { formatDateOrDash } from "@shared-core/domain/formatters";

/** Groups render worst-first (see CsrHome.tsx) but can otherwise run long —
 *  cap the default view and let the CSR expand in place rather than
 *  scrolling past dozens of rows to reach the next group. */
export const BOARD_SECTION_PREVIEW_COUNT = 5;

/**
 * Shared section chrome: uppercase heading with a count, an empty-state
 * message, a preview-capped row list, and a "Show all N →" / "Show less"
 * expander. Used by `BoardSection` (job lanes, in PortfolioBoard.tsx) and
 * `SalesOrdersNeedingAttentionSection` so they read as one visual family.
 * Expansion state is owned by the caller so `BoardSection` can expose an
 * imperative `expandAndScrollIntoView` (for the stat strip's jump-to-lane
 * behaviour) while a section that is never a focus target keeps a plain
 * local `useState`.
 */
export function ExpandableSection<T>({
  title,
  items,
  emptyMessage,
  previewCount,
  expanded,
  onToggleExpanded,
  renderItem,
  totalCount,
}: {
  title: string;
  items: T[];
  emptyMessage: string;
  previewCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  renderItem: (item: T) => ReactElement;
  /** The true total behind a server-capped preview array, when it differs
   *  from `items.length` (e.g. PortfolioBoard's `not_production_ready`
   *  lane, whose array is a worst-first preview capped well below the real
   *  count). Defaults to `items.length`. Only changes the heading's
   *  parenthetical — "Show all N" still reflects what's actually in
   *  `items`, since that's all expanding in place can reveal. */
  totalCount?: number;
}) {
  const hasMore = items.length > previewCount;
  const visibleItems =
    expanded || !hasMore ? items : items.slice(0, previewCount);

  return (
    <>
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        {title}
        <span className="ml-2 font-normal normal-case tracking-normal text-foreground">
          ({totalCount ?? items.length})
        </span>
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          {emptyMessage}
        </p>
      ) : (
        <>
          <div className="space-y-1.5">{visibleItems.map(renderItem)}</div>
          {hasMore && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="mt-2 text-sm text-primary hover:underline"
            >
              {expanded ? "Show less" : `Show all ${items.length} →`}
            </button>
          )}
        </>
      )}
    </>
  );
}

/** Row severity dot colour: `PortfolioBoard`'s job rows, whose severity is
 *  deadline-driven, and the sales-orders section's rows, which are always
 *  amber (every row is action-needed). */
export const SEVERITY_DOT: Record<"red" | "amber" | "neutral", string> = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  neutral: "bg-green-500",
};

/** A date rendered with its year (e.g. "29 Aug 2025"), not a relative "3d"
 *  delta. Showing the year is what lets a CSR spot a wrong-year Tharstern
 *  slip at a glance (see `needed_by_year_suspect`). */
export function formatDateWithYear(dateIso: string): string {
  return formatDateOrDash(dateIso);
}
